import type { Vector3Tuple } from "three";
import { create } from "zustand";

import type {
  AgentState,
  AgentStatus,
  AnimationName,
  PropId,
} from "@/types/engine.types";

interface EngineStore {
  agents: Record<string, AgentState>;
  selectedAgentId: string | null;
  selectCount: number;
  moveTargetMarker: Vector3Tuple | null;
  activePropIds: PropId[];
  /** Ephemeral poke-bark bubble text, keyed by agent id (null = no bubble). */
  pokeLines: Record<string, string | null>;

  setPokeLine: (args: { agentId: string; line: string | null }) => void;
  activateProp: (args: { propId: PropId }) => void;
  deactivateProp: (args: { propId: PropId }) => void;
  initializeAgent: (args: { agent: AgentState }) => void;
  selectAgent: (args: { id: string | null }) => void;
  setMoveTarget: (args: {
    agentId: string;
    target: Vector3Tuple;
    path: Vector3Tuple[];
  }) => void;
  updateAgentPosition: (args: {
    id: string;
    position: Vector3Tuple;
    rotation: number;
    speed?: number;
  }) => void;
  setAgentAnimation: (args: { id: string; animation: AnimationName }) => void;
  setAgentStatus: (args: { id: string; status: AgentStatus }) => void;
  clearMoveTarget: (args: { agentId: string }) => void;
}

export const useEngineStore = create<EngineStore>((set) => ({
  agents: {},
  selectedAgentId: null,
  selectCount: 0,
  moveTargetMarker: null,
  activePropIds: [],
  pokeLines: {},

  setPokeLine: ({ agentId, line }) =>
    set((s) => ({ pokeLines: { ...s.pokeLines, [agentId]: line } })),

  activateProp: ({ propId }) =>
    set((s) => ({
      activePropIds: s.activePropIds.includes(propId)
        ? s.activePropIds
        : [...s.activePropIds, propId],
    })),
  deactivateProp: ({ propId }) =>
    set((s) => ({
      activePropIds: s.activePropIds.filter((id) => id !== propId),
    })),

  initializeAgent: ({ agent }) =>
    set((state) => ({
      agents: { ...state.agents, [agent.id]: agent },
    })),

  selectAgent: ({ id }) => set((s) => ({ selectedAgentId: id, selectCount: s.selectCount + 1 })),

  setMoveTarget: ({ agentId, target, path }) =>
    set((state) => {
      const agent = state.agents[agentId];
      if (!agent) return state;
      return {
        agents: {
          ...state.agents,
          [agentId]: {
            ...agent,
            targetPosition: target,
            path,
            pathIndex: 0,
            status: "moving" as const,
          },
        },
        moveTargetMarker: target,
      };
    }),

  updateAgentPosition: ({ id, position, rotation, speed }) =>
    set((state) => {
      const agent = state.agents[id];
      if (!agent) return state;
      return {
        agents: {
          ...state.agents,
          [id]: { ...agent, position, rotation, ...(speed !== undefined && { speed }) },
        },
      };
    }),

  setAgentAnimation: ({ id, animation }) =>
    set((state) => {
      const agent = state.agents[id];
      if (!agent) return state;
      return {
        agents: {
          ...state.agents,
          [id]: { ...agent, currentAnimation: animation },
        },
      };
    }),

  setAgentStatus: ({ id, status }) =>
    set((state) => {
      const agent = state.agents[id];
      if (!agent) return state;
      return {
        agents: {
          ...state.agents,
          [id]: { ...agent, status },
        },
      };
    }),

  clearMoveTarget: ({ agentId }) =>
    set((state) => {
      const agent = state.agents[agentId];
      if (!agent) return state;
      return {
        agents: {
          ...state.agents,
          [agentId]: {
            ...agent,
            targetPosition: null,
            path: [],
            pathIndex: 0,
            speed: 0,
          },
        },
        moveTargetMarker: null,
      };
    }),
}));
